import { type PaginationState, type RowSelectionState, type SortingState, type VisibilityState } from '@tanstack/react-table'
import { useState } from 'react'

import TasksTable from 'components/brokers/tasks/TasksTable'
import TasksContext from 'components/context/TasksContext'
import { getTasksQueryKey } from 'components/hooks/tasks/useGetTasks'

type TasksMonitoringViewProps = {
  taskTypes: string[]
  statusFilters?: string[]
  columnVisibility: VisibilityState
  setColumnVisibility: (visibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void
  rowSelection: RowSelectionState
  setRowSelection: (selection: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void
}

const TasksMonitoringView = ({
  taskTypes,
  statusFilters = [],
  columnVisibility,
  setColumnVisibility,
  rowSelection,
  setRowSelection,
}: TasksMonitoringViewProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])

  const filters = {
    task_types: taskTypes,
    ...(statusFilters.length > 0 && { statuses: statusFilters }),
  }

  const defaultOverrides = {
    job_handler: false, // Always hide - v2 tasks don't have a job handler
    job_type: false, // Always hide - we're filtering by task type
    task_type: false, // Always hide - all tasks are the same type
  }

  // Merge user preferences with forced overrides
  const mergedColumnVisibility = {
    ...columnVisibility,
    ...defaultOverrides,
  }

  // Wrap setColumnVisibility to preserve forced overrides
  const handleColumnVisibilityChange = (updater: any) => {
    setColumnVisibility((prev: VisibilityState) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater
      // Preserve forced overrides
      return {
        ...newState,
        ...defaultOverrides,
      }
    })
  }

  return (
    <TasksContext.Provider
      value={{
        getTasksQueryKey: getTasksQueryKey({
          filters,
          pagination,
          sorting,
        }),
      }}
    >
      <div className='flex-col w-full h-full space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
        <TasksTable
          filters={filters}
          setFilters={() => {}}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          columnVisibilityOverrides={mergedColumnVisibility}
          setColumnVisibilityOverrides={handleColumnVisibilityChange}
          actionsVisibilityOverrides={{
            openInNewTab: true,
          }}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </div>
    </TasksContext.Provider>
  )
}

export default TasksMonitoringView
